/**
 * JWT Token Utilities
 * Decode and validate JWT tokens without external dependencies
 */

interface JWTPayload {
  exp?: number
  iat?: number
  sub?: string
  [key: string]: any
}

/**
 * Decode JWT token (without verification - only for reading claims)
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.')
    if (parts.length !== 3) {
      console.warn('[JWT] Invalid token format')
      return null
    }

    // Decode the payload (base64url)
    const payload = parts[1]
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('[JWT] Failed to decode token:', error)
    return null
  }
}

/**
 * Check if JWT token is expired
 * @param token - JWT token string
 * @param bufferSeconds - Buffer time in seconds before actual expiration (default: 60s)
 * @returns true if expired or will expire within buffer time
 */
export function isTokenExpired(token: string, bufferSeconds: number = 60): boolean {
  const payload = decodeJWT(token)

  if (!payload || !payload.exp) {
    console.warn('[JWT] Token has no expiration claim')
    return true // Treat tokens without exp as expired
  }

  // JWT exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000
  const currentTime = Date.now()
  const bufferTime = bufferSeconds * 1000

  // Token is expired if current time + buffer >= expiration time
  const isExpired = currentTime + bufferTime >= expirationTime

  if (isExpired) {
    const timeUntilExpiry = Math.floor((expirationTime - currentTime) / 1000)
    console.log(`[JWT] Token is expired or expiring soon (${timeUntilExpiry}s remaining)`)
  }

  return isExpired
}

/**
 * Get time until token expires in seconds
 * @param token - JWT token string
 * @returns Seconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiry(token: string): number {
  const payload = decodeJWT(token)

  if (!payload || !payload.exp) {
    return 0
  }

  const expirationTime = payload.exp * 1000
  const currentTime = Date.now()
  const secondsRemaining = Math.floor((expirationTime - currentTime) / 1000)

  return Math.max(0, secondsRemaining)
}

/**
 * Check if token needs refresh (expired or expiring soon)
 * @param token - JWT token string
 * @param refreshThresholdSeconds - Refresh if expiring within this time (default: 1 minute - industry standard)
 * @returns true if token should be refreshed
 */
export function shouldRefreshToken(token: string, refreshThresholdSeconds: number = 60): boolean {
  return isTokenExpired(token, refreshThresholdSeconds)
}
